/**
 * ExamentrainingOefenen — oefenscherm examentraining in de ECHTE toets-interface.
 *
 * Op verzoek van Yasar (28-08): "gebruik zelfde interface als testwisely, alleen
 * de vragen waarbij je dingen nodig hebt moeten veranderen, net als Facet."
 * Daarom hergebruikt dit scherm de QuestionView-componenten (QvHeader, QvFooter,
 * QvProgressBar, QvMCOption, QvTextInput, QvTextarea) waarop ook ToetsMaken,
 * InzageModule en CommunicatieResultaat draaien. Leerlingen zien dus exact het
 * afname-scherm dat ze kennen; alleen het antwoordwidget wisselt per vraagtype
 * en er is een Nakijken-stap per vraag (oefenmodus).
 *
 * Zelfstandig: voorbeeldvragen, geen backend/login. De nakijklogica is echt
 * (src/utils/examentrainingScoring.js).
 */
import React, { useState, useRef, useEffect } from 'react';
import {
  QvHeader, QvProgressBar, QvButton, QvIcon, QvBadge,
  QvMCOption, QvTextInput, QvTextarea,
} from './QuestionView';
import './QuestionView.css';
import { scoreVraag, scoreZelfPerPunt } from '../utils/examentrainingScoring';
import EtRekenmachine from '../components/examentraining/EtRekenmachine';
import EtToolbox from '../components/examentraining/EtToolbox';
import EtStartFlow from '../components/examentraining/EtStartFlow';
import EtInleverDialoog from '../components/examentraining/EtInleverDialoog';
import EtEindresultaat from '../components/examentraining/EtEindresultaat';
import EtFormulekaart from '../components/examentraining/EtFormulekaart';
import EtHeader from '../components/examentraining/EtHeader';
import EtVraagNav from '../components/examentraining/EtVraagNav';
import { EtMeetlat, EtGeodriehoek, EtWindroos, EtLetterplankje } from '../components/examentraining/EtInstrumenten';
import './ExamentrainingOefenen.css';

const OPGAVE = {
  nummer: 'Opgave 4',
  inleiding:
    'Een zwembad heeft de vorm van een rechthoek. Het bad is 25 meter lang en ' +
    '10 meter breed. Het water staat overal 1,4 meter hoog.',
  bron: 'CE wiskunde vmbo bb 2024, tijdvak 1 — Examenblad.nl (bewerking voorbeeld)',
  vragen: [
    { id: 'v1', vraagnummer: '12', onderwerp: 'Meetkunde 2D',
      vraagtype: 'getal_met_eenheid',
      vraagtekst: 'Bereken de oppervlakte van de bodem van het zwembad.',
      verwacht_getal: 250, eenheid: 'm²', maximumscore: 2, hulpmiddelen: ['rekenmachine', 'formulekaart', 'meetlat', 'geodriehoek', 'windroos', 'letterplankje'],
      model: [{ stap: 1, omschrijving: '25 × 10', punten: 1 },
              { stap: 2, omschrijving: '= 250 (m²)', punten: 1 }] },
    { id: 'v2', vraagnummer: '13', onderwerp: 'Meetkunde 3D',
      vraagtype: 'berekening_met_stappen',
      vraagtekst: 'Bereken hoeveel kubieke meter water er in het bad zit. Schrijf je berekening op.',
      maximumscore: 3, hulpmiddelen: ['rekenmachine', 'toolbox', 'formulekaart', 'meetlat', 'geodriehoek', 'windroos', 'letterplankje'],
      model: [{ stap: 1, omschrijving: 'oppervlakte bodem × hoogte, dus 250 × 1,4', punten: 1 },
              { stap: 2, omschrijving: 'de vermenigvuldiging goed uitgevoerd', punten: 1 },
              { stap: 3, omschrijving: 'antwoord: 350 m³', punten: 1 }] },
    { id: 'v3', vraagnummer: '14', onderwerp: 'Rekenen',
      vraagtype: 'getal',
      vraagtekst: 'Door verdamping verdwijnt 2% van het water. Hoeveel m³ is dat? Rond af op één decimaal.',
      verwacht_getal: 7, tolerantie_abs: 0.05, maximumscore: 2, hulpmiddelen: ['rekenmachine', 'formulekaart', 'meetlat', 'geodriehoek', 'windroos', 'letterplankje'],
      model: [{ stap: 1, omschrijving: '2% van 350 = 0,02 × 350', punten: 1 },
              { stap: 2, omschrijving: '= 7 (m³)', punten: 1 }] },
    { id: 'v4', vraagnummer: '15', onderwerp: 'Meetkunde 2D',
      vraagtype: 'meerkeuze',
      vraagtekst: 'Welke vorm heeft de bodem van het zwembad?',
      opties: ['een driehoek', 'een rechthoek', 'een cirkel', 'een trapezium'],
      correcte_optie: 'B', maximumscore: 1,
      model: [{ stap: 1, omschrijving: 'B — een rechthoek', punten: 1 }] },
  ],
};

export default function ExamentrainingOefenen() {
  const [i, setI] = useState(0);
  const [antwoorden, setAntwoorden] = useState({});   // per vraag-id: invoer
  const [nagekeken, setNagekeken] = useState({});     // per vraag-id: true
  const [zelf, setZelf] = useState({});               // per vraag-id: {stap: bool}
  const [calcOpen, setCalcOpen] = useState(false);
  const [toolboxHtml, setToolboxHtml] = useState({});   // per vraag-id: rijke inhoud
  const [gemarkeerd, setGemarkeerd] = useState({});     // per vraag-id: vlag (Facet: markeer opgave)
  const [toonOverzicht, setToonOverzicht] = useState(false);
  const [gestart, setGestart] = useState(false);        // startflow doorlopen?
  const [startInstellingen, setStartInstellingen] = useState(null); // {volume, stem} uit de startflow
  const [dialoogOpen, setDialoogOpen] = useState(false);
  const [ingeleverd, setIngeleverd] = useState(false);
  const [terugblik, setTerugblik] = useState(null);     // vraagindex in terugblik-modus
  const [fkOpen, setFkOpen] = useState(false);          // formulekaart
  const [instrument, setInstrument] = useState({});     // {meetlat:bool, geodriehoek:bool, windroos:bool, letterplankje:bool}
  const toolboxRef = useRef(null);
  const uitslagRef = useRef(null);

  const vraag = OPGAVE.vragen[i];
  const antwoord = antwoorden[vraag.id] ?? '';
  const isNagekeken = !!nagekeken[vraag.id];
  const isZelfscore = vraag.vraagtype === 'berekening_met_stappen';
  const isLaatste = i === OPGAVE.vragen.length - 1;
  const model = Array.isArray(vraag.model) ? vraag.model : [];
  // Review-fix #16: 'gemaakt' = er staat een antwoord, niet pas na Nakijken.
  const isBeantwoord = (v) => String(antwoorden[v.id] ?? '').trim() !== '';
  const statusVan = (v) => (gemarkeerd[v.id] ? 'gemarkeerd' : isBeantwoord(v) ? 'gemaakt' : 'niet-gemaakt');
  const STATUS_TEKST = { gemarkeerd: 'gemarkeerd', gemaakt: 'gemaakt', 'niet-gemaakt': 'nog niet gemaakt' };
  const aantalOnbeantwoord = OPGAVE.vragen.filter((v) => !isBeantwoord(v)).length;

  // Review-fix #15: het rapport rekent élk gegeven antwoord na — automatisch
  // waar dat kan; alleen een zelfscore-vraag zónder zelfscore blijft 'niet
  // nagekeken' (null) in plaats van stil 0.
  const scoreVoorRapport = (v) => {
    if (!isBeantwoord(v)) return 0;
    if (v.vraagtype === 'berekening_met_stappen') {
      return nagekeken[v.id] ? scoreZelfPerPunt(v.model, zelf[v.id] || {}).score : null;
    }
    return scoreVraag(antwoorden[v.id] ?? '', v)?.score ?? 0;
  };

  const answeredSet = new Set(
    OPGAVE.vragen.map((v, idx) => (isBeantwoord(v) ? idx : null)).filter((x) => x !== null));

  useEffect(() => {
    if (isNagekeken && uitslagRef.current) uitslagRef.current.focus();
  }, [isNagekeken]);

  const resultaat = !isNagekeken ? null
    : isZelfscore ? scoreZelfPerPunt(model, zelf[vraag.id] || {})
    : scoreVraag(antwoord, vraag);

  const zetAntwoord = (val) => setAntwoorden((a) => ({ ...a, [vraag.id]: val }));
  const kijkNa = () => setNagekeken((n) => ({ ...n, [vraag.id]: true }));
  const gaNaar = (idx) => setI(Math.max(0, Math.min(OPGAVE.vragen.length - 1, idx)));
  const heeftRekenmachine = (vraag.hulpmiddelen || []).includes('rekenmachine');
  const heeftFormulekaart = (vraag.hulpmiddelen || []).includes('formulekaart');


  const zetInstrument = (naam) => setInstrument((s2) => ({ ...s2, [naam]: !s2[naam] }));
  const kanVoorlezen = !!(window.speechSynthesis && startInstellingen);
  const heeft = (h) => (vraag.hulpmiddelen || []).includes(h);
  const gemarkeerdSet = new Set(
    OPGAVE.vragen.map((v, idx) => (gemarkeerd[v.id] ? idx : null)).filter((x) => x !== null));

  // Letterplankje: teken landt in de toolbox (op de cursor) of achteraan het antwoord.
  const voegTekenIn = (teken) => {
    if (isNagekeken) return;
    if (isZelfscore && heeft('toolbox') && toolboxRef.current) toolboxRef.current.voegTekenIn(teken);
    else setAntwoorden((a) => ({ ...a, [vraag.id]: (a[vraag.id] ?? '') + teken }));
  };

  // De stemkeuze uit de startflow wordt écht gebruikt: de voorleesknop leest
  // de opgave en de vraag voor met de gekozen stem en het gekozen volume.
  const leesVoor = () => {
    if (!kanVoorlezen) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(`${OPGAVE.inleiding} ${vraag.vraagtekst}`);
    if (startInstellingen.stem) u.voice = startInstellingen.stem;
    u.volume = startInstellingen.volume ?? 1;
    window.speechSynthesis.speak(u);
  };

  const headerTools = [
    kanVoorlezen && { naam: 'voorlezen', label: 'Lees de opgave en de vraag voor', onClick: leesVoor },
    heeft('formulekaart') && { naam: 'formulekaart', label: 'Formulekaart', actief: fkOpen, onClick: () => setFkOpen((v) => !v) },
    heeft('rekenmachine') && { naam: 'rekenmachine', label: 'Rekenmachine', actief: calcOpen, onClick: () => setCalcOpen((v) => !v) },
    heeft('meetlat') && { naam: 'meetlat', label: 'Meetlat', actief: !!instrument.meetlat, onClick: () => zetInstrument('meetlat') },
    heeft('geodriehoek') && { naam: 'geodriehoek', label: 'Geodriehoek', actief: !!instrument.geodriehoek, onClick: () => zetInstrument('geodriehoek') },
    heeft('windroos') && { naam: 'windroos', label: 'Windroos', actief: !!instrument.windroos, onClick: () => zetInstrument('windroos') },
    heeft('letterplankje') && { naam: 'letterplankje', label: 'Letterplankje', actief: !!instrument.letterplankje, onClick: () => zetInstrument('letterplankje') },
  ].filter(Boolean);

  // Facet-principe: de berekening uit de rekenmachine landt in het antwoord.
  // Bij een berekenvraag als extra regel ("0,02×350 = 7"); bij een getalvraag
  // zetten we de uitkomst in het invoerveld.
  const plakUitRekenmachine = (regel, resultaatStr) => {
    if (isNagekeken) return;
    if (isZelfscore && (vraag.hulpmiddelen || []).includes('toolbox') && toolboxRef.current) {
      toolboxRef.current.voegRegelToe(regel);           // landt in de toolbox zelf
    } else if (isZelfscore) {
      setAntwoorden((a) => ({ ...a, [vraag.id]: (a[vraag.id] ? a[vraag.id] + '\n' : '') + regel }));
    } else {
      setAntwoorden((a) => ({ ...a, [vraag.id]: resultaatStr }));
    }
  };
  const volgende = () => { if (isLaatste) setToonOverzicht(true); else gaNaar(i + 1); };

  // Ná inleveren: terugblik op één vraag, of het eindresultaat-rapport.
  if (ingeleverd) {
    const reset = () => {
      setI(0); setIngeleverd(false); setTerugblik(null); setDialoogOpen(false);
      setAntwoorden({}); setNagekeken({}); setZelf({}); setGemarkeerd({});
      setToolboxHtml({}); setToonOverzicht(false);
    };

    if (terugblik !== null) {
      const tv = OPGAVE.vragen[terugblik];
      const tvScore = scoreVoorRapport(tv);
      const tvModel = Array.isArray(tv.model) ? tv.model : [];
      return (
        <div className="tw-qv-root toets-maken-container">
          <QvHeader examName="Examentraining · wiskunde vmbo bb" studentName="Oefenmodus"
            current={terugblik} total={OPGAVE.vragen.length} seconds={null} />
          <QvProgressBar value={terugblik + 1} total={OPGAVE.vragen.length} />
          <div className="tw-qv-passage">
            <div className="tw-qv-passage-header etq-passage-kop">
              <span className="tw-qv-passage-label">{OPGAVE.nummer}</span>
              <QvBadge tone="blue">{tv.onderwerp}</QvBadge>
              <span className="etq-oefen-badge">Terugblik — ingeleverd</span>
            </div>
            <div className="tw-qv-passage-text">{OPGAVE.inleiding}</div>
          </div>
          <main style={{ flex: 1, minHeight: 0, display: 'flex', justifyContent: 'center',
            padding: 'var(--tw-body-pad, 20px)', overflow: 'auto' }}>
            <div style={{ width: '100%', maxWidth: 'var(--tw-body-max, 1040px)',
              background: 'var(--tw-surface)', borderRadius: 'var(--tw-card-radius, 12px)',
              boxShadow: 'var(--tw-card-shadow)', padding: 'var(--tw-card-pad, 22px 26px)',
              display: 'flex', flexDirection: 'column', gap: 16, alignSelf: 'flex-start' }}>
              <p style={{ margin: 0, fontSize: 'var(--tw-q-size, 17px)', lineHeight: 1.55,
                color: 'var(--tw-text)', maxWidth: '68ch' }}>
                <strong style={{ display: 'block', fontSize: 12, color: 'var(--tw-text-muted)',
                  marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
                  Vraag {tv.vraagnummer} · {tv.maximumscore} {tv.maximumscore === 1 ? 'punt' : 'punten'}
                </strong>
                {tv.vraagtekst}
              </p>
              <div className="etq-terugblik-antwoord">
                <h4>Jouw antwoord</h4>
                <p>{String(antwoorden[tv.id] ?? '').trim() || <i>Niet beantwoord</i>}</p>
              </div>
              <section className="etq-uitslag neutraal">
                <p className="etq-score">
                  Score: {tvScore === null ? 'zelf na te kijken' : `${tvScore} van de ${tv.maximumscore}`}
                </p>
                <h4 className="etq-model-kop">Antwoordmodel</h4>
                {tvModel.map((r) => (
                  <div key={r.stap} className="etq-modelrij"><span>{r.omschrijving}</span><b>{r.punten} pt</b></div>
                ))}
              </section>
            </div>
          </main>
          <footer className="etq-footer">
            <QvButton variant="secondary" onClick={() => setTerugblik(Math.max(0, terugblik - 1))}
              disabled={terugblik === 0} ariaLabel="Vorige vraag in terugblik">
              <QvIcon name="arrow-left" size={16} stroke={2} /> Vorige
            </QvButton>
            <QvButton variant="secondary" onClick={() => setTerugblik(null)}
              ariaLabel="Terug naar het resultaat">Terug naar resultaat</QvButton>
            <QvButton variant="secondary" onClick={() => setTerugblik(Math.min(OPGAVE.vragen.length - 1, terugblik + 1))}
              disabled={terugblik === OPGAVE.vragen.length - 1} ariaLabel="Volgende vraag in terugblik"
              style={{ justifySelf: 'end' }}>
              Volgende <QvIcon name="arrow-right" size={16} stroke={2} />
            </QvButton>
          </footer>
        </div>
      );
    }

    const totaalScore = OPGAVE.vragen.reduce((som, v) => som + (scoreVoorRapport(v) ?? 0), 0);
    const totaalMax = OPGAVE.vragen.reduce((som, v) => som + v.maximumscore, 0);
    return (
      <div className="tw-qv-root toets-maken-container">
        <QvHeader examName="Examentraining · wiskunde vmbo bb" studentName="Oefenmodus"
          current={OPGAVE.vragen.length - 1} total={OPGAVE.vragen.length} seconds={null} />
        <QvProgressBar value={OPGAVE.vragen.length} total={OPGAVE.vragen.length} />
        <main style={{ flex: 1, minHeight: 0, display: 'flex', justifyContent: 'center',
          padding: 'var(--tw-body-pad, 20px)', overflow: 'auto' }}>
          <EtEindresultaat
            examNaam="Examentraining · wiskunde vmbo bb"
            deelnemer="Oefenmodus"
            opgaven={[{
              nummer: OPGAVE.nummer, titel: '',
              vragen: OPGAVE.vragen.map((v, idx) => ({
                index: idx, vraagnummer: v.vraagnummer, max: v.maximumscore,
                score: scoreVoorRapport(v),
              })),
            }]}
            totaal={totaalScore} max={totaalMax}
            onNaarVraag={setTerugblik} onOpnieuw={reset}
          />
        </main>
      </div>
    );
  }

  // Startflow: de controleschermen die leerlingen van het echte examen kennen.
  if (!gestart) {
    return (
      <EtStartFlow
        examName="Examentraining · wiskunde vmbo bb"
        deelnemer="Oefenmodus"
        toetsInfo={{
          aantalVragen: OPGAVE.vragen.length,
          maxPunten: OPGAVE.vragen.reduce((som, v) => som + v.maximumscore, 0),
          duurMinuten: null,
        }}
        onKlaar={(instellingen) => { setStartInstellingen(instellingen); setGestart(true); }}
      />
    );
  }

  // Overzicht vóór inleveren — status per vraag, legenda, en pas hier Inleveren.
  if (toonOverzicht) {
    return (
      <div className="tw-qv-root toets-maken-container">
        <QvHeader examName="Examentraining · wiskunde vmbo bb" studentName="Oefenmodus"
          current={i} total={OPGAVE.vragen.length} seconds={null} />
        <QvProgressBar value={OPGAVE.vragen.length} total={OPGAVE.vragen.length} />
        <main style={{ flex: 1, minHeight: 0, display: 'flex', justifyContent: 'center',
          padding: 'var(--tw-body-pad, 20px)', overflow: 'auto' }}>
          <div className="etq-overzicht-card">
            <h2>Overzicht</h2>
            <p>Kijk of je alle vragen hebt gemaakt. Klik op een vraag om er direct naartoe te gaan.</p>
            <div className="etq-overzicht-raster" role="list">
              {OPGAVE.vragen.map((v, idx) => (
                <button key={v.id} role="listitem"
                  className={`etq-overzicht-vraag ${statusVan(v) === 'gemaakt' ? 'beantwoord' : statusVan(v) === 'gemarkeerd' ? 'gemarkeerd' : ''}`}
                  onClick={() => { setToonOverzicht(false); gaNaar(idx); }}
                  aria-label={`Vraag ${v.vraagnummer}, ${v.maximumscore} ${v.maximumscore === 1 ? 'punt' : 'punten'}, ${STATUS_TEKST[statusVan(v)]}`}>
                  <b>{v.vraagnummer}</b>
                  <span>{v.maximumscore}p</span>
                </button>
              ))}
            </div>
            <div className="etq-legenda" aria-label="Legenda">
              <span><i className="dot onbeantwoord" /> Nog niet gemaakt</span>
              <span><i className="dot beantwoord" /> Gemaakt</span>
              <span><i className="dot gemarkeerd" /> Gemarkeerd</span>
            </div>
          </div>
        </main>
        <footer className="etq-footer">
          <QvButton variant="secondary" onClick={() => setToonOverzicht(false)}
            ariaLabel="Terug naar de vragen">
            <QvIcon name="arrow-left" size={16} stroke={2} /> Terug
          </QvButton>
          <span />
          <QvButton variant="submit" onClick={() => setDialoogOpen(true)}
            ariaLabel="Lever je antwoorden in">
            Inleveren <QvIcon name="check" size={16} stroke={2.25} />
          </QvButton>
        </footer>
        <EtInleverDialoog open={dialoogOpen} aantalOnbeantwoord={aantalOnbeantwoord}
          onTerug={() => setDialoogOpen(false)}
          onInleveren={() => { setDialoogOpen(false); setToonOverzicht(false); setIngeleverd(true); }} />
      </div>
    );
  }

  return (
    <div className="tw-qv-root toets-maken-container">
      <EtHeader examName="Examentraining wiskunde" deelnemer="Oefenmodus"
        huidig={i} totaal={OPGAVE.vragen.length}
        punten={`${vraag.maximumscore}p`} tools={headerTools} />
      <QvProgressBar value={i + 1} total={OPGAVE.vragen.length} />

      <main style={{ flex: 1, minHeight: 0, display: 'flex', justifyContent: 'center',
        padding: 'var(--tw-body-pad, 20px)', overflow: 'auto' }}>
        <div style={{
          width: '100%', maxWidth: 'var(--tw-body-max, 1040px)',
          background: 'var(--tw-surface)',
          borderRadius: 'var(--tw-card-radius, 12px)',
          boxShadow: 'var(--tw-card-shadow, 0 1px 3px rgba(0,0,0,.08))',
          padding: 'var(--tw-card-pad, 22px 26px)',
        }} className="etq-kaart-split">
          {/* Links: de opgave-context — zoals op het echte examen ín de kaart */}
          <div className="etq-context">
            <p className="etq-context-kop"><strong>{OPGAVE.nummer}</strong>
              <QvBadge tone="blue">{vraag.onderwerp}</QvBadge></p>
            <p className="etq-context-tekst">{OPGAVE.inleiding}</p>
            <p className="etq-context-bron">Bron: {OPGAVE.bron}</p>
          </div>

          {/* Rechts: vraag + antwoord */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
          {/* Vraagkop — zelfde patroon als promptHeader in ToetsMaken */}
          <p style={{ margin: 0, fontSize: 'var(--tw-q-size, 17px)', lineHeight: 1.55,
            color: 'var(--tw-text)', maxWidth: '68ch' }}>
            <strong style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12,
              color: 'var(--tw-text-muted)', marginBottom: 6, textTransform: 'uppercase',
              letterSpacing: '0.05em', fontWeight: 600 }}>
              Vraag {vraag.vraagnummer} · {vraag.maximumscore} {vraag.maximumscore === 1 ? 'punt' : 'punten'}
              <button type="button"
                className={`etq-vlag ${gemarkeerd[vraag.id] ? 'aan' : ''}`}
                aria-pressed={!!gemarkeerd[vraag.id]}
                onClick={() => setGemarkeerd((g) => ({ ...g, [vraag.id]: !g[vraag.id] }))}
                aria-label="Markeer deze vraag om er later naar terug te gaan">
                ⚑ {gemarkeerd[vraag.id] ? 'Gemarkeerd' : 'Markeer'}
              </button>
            </strong>
            {vraag.vraagtekst}
          </p>

          {/* Antwoordwidget per vraagtype — dit is het enige dat wisselt (Facet-principe) */}
          {(vraag.vraagtype === 'getal' || vraag.vraagtype === 'getal_met_eenheid') && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, maxWidth: 320 }}>
              <QvTextInput
                ariaLabel={`Antwoord op vraag ${vraag.vraagnummer}`}
                value={antwoord}
                onChange={isNagekeken ? () => {} : zetAntwoord}
                placeholder="Typ hier je antwoord…"
              />
              {vraag.eenheid && (
                <span style={{ fontSize: 17, color: 'var(--tw-text-muted)', flexShrink: 0 }}>
                  {vraag.eenheid}
                </span>
              )}
            </div>
          )}

          {vraag.vraagtype === 'meerkeuze' && (
            <div role="radiogroup" aria-label="Antwoordopties" className="tw-qv-mc-grid"
              style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, alignContent: 'start' }}>
              {vraag.opties.map((opt, idx) => {
                const letter = String.fromCharCode(65 + idx);
                return (
                  <QvMCOption key={letter} letter={letter} label={opt}
                    selected={antwoord === letter}
                    onSelect={isNagekeken ? () => {} : () => zetAntwoord(letter)} />
                );
              })}
            </div>
          )}

          {isZelfscore && ((vraag.hulpmiddelen || []).includes('toolbox') ? (
            <EtToolbox
              ref={toolboxRef}
              ariaLabel={`Berekening voor vraag ${vraag.vraagnummer}`}
              html={toolboxHtml[vraag.id] || ''}
              disabled={isNagekeken}
              onWijzig={(htmlNieuw, tekst) => {
                setToolboxHtml((t) => ({ ...t, [vraag.id]: htmlNieuw }));
                zetAntwoord(tekst);   // platte vorm voor nakijken/opslag
              }}
            />
          ) : (
            <QvTextarea
              ariaLabel={`Berekening voor vraag ${vraag.vraagnummer}`}
              value={antwoord}
              onChange={isNagekeken ? () => {} : zetAntwoord}
              placeholder="Schrijf hier je berekening op…"
              rows={5}
            />
          ))}

          {!isNagekeken && (
            <div>
              <QvButton variant="primary" onClick={kijkNa}
                disabled={!String(antwoord).trim()} ariaLabel="Kijk deze vraag na">
                <QvIcon name="check" size={16} stroke={2} /> Nakijken
              </QvButton>
            </div>
          )}

          {/* Uitslag + antwoordmodel — oefenmodus-extra, in dezelfde beeldtaal */}
          {isNagekeken && (
            <section ref={uitslagRef} tabIndex={-1} aria-live="polite"
              className={`etq-uitslag ${isZelfscore ? 'neutraal'
                : resultaat?.goed ? 'goed' : 'fout'}`}>
              {!isZelfscore && (
                <p className="etq-score">
                  {resultaat.goed ? '✓ Goed' : '✗ Niet goed'} — {resultaat.score} van de {resultaat.max} punten
                  <span className="etq-reden"> ({resultaat.reden})</span>
                </p>
              )}
              <h4 className="etq-model-kop">Antwoordmodel</h4>
              {isZelfscore ? (
                <>
                  <p className="etq-zelfhint">Kijk je eigen berekening na. Vink aan wat je goed had.<br /><small>Let op de examenregel: voor elke <b>rekenfout</b> gaat 1 punt eraf. Een <b>notatiefout</b> die geen invloed heeft op je eindantwoord kost geen punt.</small></p>
                  {model.map((r) => (
                    <label key={r.stap} className="etq-modelrij klik">
                      <input type="checkbox" checked={!!(zelf[vraag.id] || {})[r.stap]}
                        onChange={(e) => setZelf((z) => ({
                          ...z, [vraag.id]: { ...(z[vraag.id] || {}), [r.stap]: e.target.checked },
                        }))} />
                      <span>{r.omschrijving}</span><b>{r.punten} pt</b>
                    </label>
                  ))}
                  <p className="etq-score">Jouw score: {resultaat.score} van de {resultaat.max} punten</p>
                </>
              ) : (
                model.map((r) => (
                  <div key={r.stap} className="etq-modelrij">
                    <span>{r.omschrijving}</span><b>{r.punten} pt</b>
                  </div>
                ))
              )}
            </section>
          )}
          </div>
        </div>
      </main>

      <EtRekenmachine open={calcOpen && heeftRekenmachine && !isNagekeken}
        onSluit={() => setCalcOpen(false)} onPlak={plakUitRekenmachine} />
      <EtFormulekaart open={fkOpen && heeftFormulekaart} onSluit={() => setFkOpen(false)} />
      <EtMeetlat open={!!instrument.meetlat && heeft('meetlat')} onSluit={() => zetInstrument('meetlat')} />
      <EtGeodriehoek open={!!instrument.geodriehoek && heeft('geodriehoek')} onSluit={() => zetInstrument('geodriehoek')} />
      <EtWindroos open={!!instrument.windroos && heeft('windroos')} onSluit={() => zetInstrument('windroos')} />
      <EtLetterplankje open={!!instrument.letterplankje && heeft('letterplankje')}
        onSluit={() => zetInstrument('letterplankje')} onTeken={voegTekenIn} />

      <footer className="etq-footer">
        <QvButton variant="secondary" onClick={() => gaNaar(i - 1)} disabled={i === 0}
          ariaLabel="Ga naar vorige vraag">
          <QvIcon name="arrow-left" size={16} stroke={2} /> Vorige
        </QvButton>
        <EtVraagNav huidig={i} totaal={OPGAVE.vragen.length}
          beantwoordSet={answeredSet} gemarkeerdSet={gemarkeerdSet}
          labels={OPGAVE.vragen.map((v) => v.vraagnummer)} onSpring={gaNaar} />
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <QvButton variant="secondary" onClick={() => setToonOverzicht(true)}
            ariaLabel="Bekijk het overzicht van alle vragen">Overzicht</QvButton>
          {isLaatste ? (
            <QvButton variant="submit" onClick={volgende} ariaLabel="Naar het overzicht om in te leveren">
              Inleveren <QvIcon name="check" size={16} stroke={2.25} />
            </QvButton>
          ) : (
            <QvButton variant="primary" onClick={volgende} ariaLabel="Ga naar volgende vraag">
              Volgende <QvIcon name="arrow-right" size={16} stroke={2} />
            </QvButton>
          )}
        </div>
      </footer>
    </div>
  );
}
